import { AfterViewInit, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { forkJoin, of, Subject } from 'rxjs';
import { catchError, finalize, map, switchMap, takeUntil } from 'rxjs/operators';
import { BaseComponent } from 'src/app/core/base.component';
import { GlobalDataService } from 'src/app/shared/services/global-data.service';
import { SessionService } from 'src/app/shared/services/session.service';
import { RegistrationsList } from '../../registrations-list';
import { RegistrationService } from '../registration.service';
import { QueryFilter } from 'src/app/shared/models/query-filter.model';
import { StringHelpers } from 'src/app/shared/helpers/string-helpers';
import { EditTeamModalComponent, EditTeamModalData } from 'src/app/shared/modals/edit-team-modal/edit-team-modal.component';

export interface TeamVm {
  captain: string;
  members: RegistrationsList[];
  startingHoleSummary: string;
  /** First agreed hole for assign / display default */
  primaryStartingHole: string;
}

@Component({
  selector: 'app-registrations-teams',
  templateUrl: './registrations-teams.component.html',
  styleUrls: ['./registrations-teams.component.scss'],
  standalone: false
})
export class RegistrationsTeamsComponent extends BaseComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() filter: QueryFilter = new QueryFilter();
  public allRegistrations: RegistrationsList[] = [];
  public teams: TeamVm[] = [];
  public unassignedPlayers: RegistrationsList[] = [];
  /** mat-select model: player id → option value */
  public assignChoice: Record<string, string> = {};

  private readonly destroyed$ = new Subject<void>();

  constructor(
    public sessionService: SessionService,
    private globalDataService: GlobalDataService,
    private registrationService: RegistrationService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    stringHelpers: StringHelpers
  ) {
    super(sessionService, stringHelpers);
  }

  ngOnInit(): void {
    if (this.isGlobalOutingSet()) {
      this.selectedIteration = this.getGlobalOutingIteration();
      this.loadAllRegistrations();
    }
  }

  ngAfterViewInit(): void {
    this.globalDataService.iterationSelectionEvent.pipe(takeUntil(this.destroyed$)).subscribe((result) => {
      this.selectedIteration = result;
      this.loadAllRegistrations();
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  loadAllRegistrations(): void {
    if (!this.selectedIteration?.id) {
      return;
    }
    this.isLoading = true;
    const pageSize = 500;
    const base = new QueryFilter();
    base.page = 1;
    base.pageSize = pageSize;
    base.sortColumn = 'teamCaptain';
    base.sortDirection = 'ASC';

    this.registrationService
      .getAllByIteration(this.selectedIteration.id, base)
      .pipe(
        switchMap((result: any) => {
          const rows: RegistrationsList[] = [...(result.results || [])];
          const total = result.totalCount ?? rows.length;
          const pagesNeeded = Math.min(Math.ceil(total / pageSize), 50);
          if (pagesNeeded <= 1) {
            return of(rows);
          }
          const rest = [];
          for (let p = 2; p <= pagesNeeded; p++) {
            const f = new QueryFilter();
            f.page = p;
            f.pageSize = pageSize;
            f.sortColumn = 'teamCaptain';
            f.sortDirection = 'ASC';
            rest.push(this.registrationService.getAllByIteration(this.selectedIteration.id, f));
          }
          return forkJoin(rest).pipe(
            map((chunks: any[]) => {
              for (const c of chunks) {
                rows.push(...(c.results || []));
              }
              return rows;
            })
          );
        }),
        catchError(() => of([] as RegistrationsList[])),
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe((rows) => {
        this.allRegistrations = rows;
        this.rebuildTeams();
      });
  }

  private rebuildTeams(): void {
    const byCaptain = new Map<string, RegistrationsList[]>();
    const unassigned: RegistrationsList[] = [];
    for (const r of this.allRegistrations) {
      const captain = (r.teamCaptain || '').trim();
      if (!captain) {
        unassigned.push(r);
        continue;
      }
      if (!byCaptain.has(captain)) {
        byCaptain.set(captain, []);
      }
      byCaptain.get(captain)!.push(r);
    }
    const teams: TeamVm[] = [];
    for (const [captain, members] of byCaptain) {
      const sorted = [...members].sort((a, b) => {
        const ln = a.lastName.localeCompare(b.lastName);
        return ln !== 0 ? ln : a.firstName.localeCompare(b.firstName);
      });
      teams.push({
        captain,
        members: sorted,
        startingHoleSummary: this.startingHoleSummary(sorted),
        primaryStartingHole: this.primaryStartingHole(sorted)
      });
    }
    teams.sort((a, b) => a.captain.localeCompare(b.captain));
    this.teams = teams;
    this.unassignedPlayers = unassigned.sort((a, b) => {
      const ln = a.lastName.localeCompare(b.lastName);
      return ln !== 0 ? ln : a.firstName.localeCompare(b.firstName);
    });
  }

  private startingHoleSummary(members: RegistrationsList[]): string {
    const holes = [...new Set(members.map((m) => String(m.startingHole ?? '').trim()).filter(Boolean))];
    if (holes.length === 0) {
      return 'Not set';
    }
    if (holes.length === 1) {
      return holes[0];
    }
    return holes.join(', ');
  }

  private primaryStartingHole(members: RegistrationsList[]): string {
    const holes = members.map((m) => String(m.startingHole ?? '').trim()).filter(Boolean);
    return holes[0] || '';
  }

  editTeam(team: TeamVm): void {
    const ref = this.dialog.open(EditTeamModalComponent, {
      width: 'min(480px, 92vw)',
      disableClose: true,
      data: {
        members: team.members,
        outingIterationId: this.selectedIteration.id,
        lastEditedBy: this.sessionService.getUserId()
      } satisfies EditTeamModalData
    });
    ref.afterClosed().subscribe((saved) => {
      if (saved) {
        this.snackBar.open('Team updated', 'Dismiss', { duration: 4000 });
        this.loadAllRegistrations();
      }
    });
  }

  newTeamOptionValue(player: RegistrationsList): string {
    return `new:${player.id}`;
  }

  applyAssign(player: RegistrationsList): void {
    const choice = this.assignChoice[player.id];
    if (!choice) {
      this.snackBar.open('Choose a team first', 'Dismiss', { duration: 3000 });
      return;
    }
    let teamCaptain: string;
    let startingHole: string;
    if (choice.startsWith('new:')) {
      teamCaptain = `${player.firstName} ${player.lastName}`.trim();
      startingHole = '';
    } else if (choice.startsWith('existing:')) {
      const idx = Number(choice.slice('existing:'.length));
      const team = Number.isInteger(idx) ? this.teams[idx] : undefined;
      if (!team) {
        this.snackBar.open('Invalid team selection', 'Dismiss', { duration: 3000 });
        return;
      }
      teamCaptain = team.captain;
      startingHole = team.primaryStartingHole ?? '';
    } else {
      return;
    }
    this.isLoading = true;
    const body = {
      ...player,
      teamCaptain,
      startingHole,
      outingIterationId: this.selectedIteration.id,
      lastEditedBy: this.sessionService.getUserId()
    };
    this.registrationService.update(player.id, body).subscribe({
      next: () => {
        delete this.assignChoice[player.id];
        this.snackBar.open('Player assigned to team', 'Dismiss', { duration: 4000 });
        this.loadAllRegistrations();
      },
      error: () => {
        this.isLoading = false;
        this.snackBar.open('Could not assign player', 'Dismiss', { duration: 5000 });
      }
    });
  }
}
